import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, TrendingUp, DollarSign, FileText, CheckCircle, Clock, AlertTriangle, Truck, ChevronLeft, ChevronRight } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, LineChart, Line } from 'recharts';

const localeForCalendar = (lang) => lang === 'es' ? 'es-ES' : 'en-GB';

// Calendar Component
const ServiceCalendar = ({ orders, onServiceClick }) => {
  const { t, i18n } = useTranslation();
  const locale = localeForCalendar(i18n.language);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'day', 'week', 'month'
  const [selectedDate, setSelectedDate] = useState(new Date());

  const monthNames = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(2000, i, 1);
    return d.toLocaleDateString(locale, { month: 'long' });
  });

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getOrdersForDate = (date) => {
    return orders.filter(order => {
      if (!order.service_date) return false;
      const orderDate = new Date(order.service_date);
      return orderDate.getDate() === date.getDate() &&
             orderDate.getMonth() === date.getMonth() &&
             orderDate.getFullYear() === date.getFullYear();
    });
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const previousPeriod = () => {
    if (viewMode === 'day') {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() - 1);
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
    } else if (viewMode === 'week') {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() - 7);
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
    } else {
      previousMonth();
    }
  };

  const nextPeriod = () => {
    if (viewMode === 'day') {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + 1);
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
    } else if (viewMode === 'week') {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + 7);
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
    } else {
      nextMonth();
    }
  };

  const getWeekDates = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    const monday = new Date(start.setDate(diff));
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  const getPeriodLabel = () => {
    if (viewMode === 'day') {
      return selectedDate.toLocaleDateString(locale, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else if (viewMode === 'week') {
      const weekDates = getWeekDates(selectedDate);
      const start = weekDates[0].toLocaleDateString(locale, { day: 'numeric', month: 'short' });
      const end = weekDates[6].toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
      return `${start} - ${end}`;
    } else {
      return `${monthNames[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
    }
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const today = new Date();

  // Create calendar grid
  const calendarDays = [];
  
  // Add empty cells for days before the first day of month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="min-h-[40px] bg-gray-50"></div>);
  }

  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOrders = getOrdersForDate(date);
    const isToday = date.toDateString() === today.toDateString();

    calendarDays.push(
      <div 
        key={day} 
        className={`min-h-[40px] border border-gray-200 p-0.5 relative ${isToday ? 'bg-blue-50 border-blue-500' : 'bg-white hover:bg-gray-50'}`}
      >
        <div className={`text-[9px] font-semibold ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
          {day}
        </div>
        <div className="space-y-0.5">
          {dayOrders.map((order, idx) => (
            <div key={idx} className="relative group">
              <div 
                className={`text-[8px] px-0.5 py-0.5 rounded truncate cursor-pointer transition-all hover:scale-105 ${
                  order.status === 'assigned' || order.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                  order.status === 'completed' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                  'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                onMouseEnter={() => setHoveredOrder(order.id)}
                onMouseLeave={() => setHoveredOrder(null)}
                onClick={() => onServiceClick && onServiceClick()}
              >
                {order.service_time}
              </div>
              
              {/* Tooltip on hover - positioned relative to this cell */}
              {hoveredOrder === order.id && (
                <div 
                  className="absolute z-[9999] bg-gray-900 text-white text-[10px] p-3 rounded-lg shadow-2xl border border-gray-700 left-full ml-2 top-0 whitespace-normal"
                  style={{
                    minWidth: '220px',
                    maxWidth: '280px'
                  }}
                  onMouseEnter={() => setHoveredOrder(order.id)}
                  onMouseLeave={() => setHoveredOrder(null)}
                >
                  <div className="space-y-2">
                    <div>
                      <div className="font-bold text-green-400 mb-1 flex items-center gap-1">
                        <span>📍</span> {t('calendar_origin')}
                      </div>
                      <div className="pl-4 text-gray-200 leading-relaxed">{order.pickup_address}</div>
                    </div>
                    <div className="border-t border-gray-700 pt-2">
                      <div className="font-bold text-blue-400 mb-1 flex items-center gap-1">
                        <span>🎯</span> {t('calendar_destination')}
                      </div>
                      <div className="pl-4 text-gray-200 leading-relaxed">{order.delivery_address}</div>
                    </div>
                    <div 
                      className="text-[8px] text-center pt-2 border-t border-gray-700 cursor-pointer hover:bg-gray-800 rounded py-1 transition-colors"
                      onClick={() => {
                        setHoveredOrder(null);
                        onServiceClick && onServiceClick();
                      }}
                    >
                      <span className="text-blue-400 font-semibold">→ {t('calendar_view_full_details')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* View Mode Selector */}
      <div className="flex items-center justify-center gap-2 mb-2">
        <Button 
          variant={viewMode === 'day' ? 'default' : 'outline'} 
          size="sm" 
          onClick={() => setViewMode('day')}
          className="h-7 px-3 text-xs"
        >
          {t('calendar_day')}
        </Button>
        <Button 
          variant={viewMode === 'week' ? 'default' : 'outline'} 
          size="sm" 
          onClick={() => setViewMode('week')}
          className="h-7 px-3 text-xs"
        >
          {t('calendar_week')}
        </Button>
        <Button 
          variant={viewMode === 'month' ? 'default' : 'outline'} 
          size="sm" 
          onClick={() => setViewMode('month')}
          className="h-7 px-3 text-xs"
        >
          {t('calendar_month')}
        </Button>
      </div>

      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={previousPeriod} className="h-7 px-2">
          <ChevronLeft className="w-3 h-3" />
        </Button>
        <h3 className="text-sm font-semibold capitalize">
          {getPeriodLabel()}
        </h3>
        <Button variant="outline" size="sm" onClick={nextPeriod} className="h-7 px-2">
          <ChevronRight className="w-3 h-3" />
        </Button>
      </div>

      {/* Calendar Grid - Month View */}
      {viewMode === 'month' && (
        <div className="grid grid-cols-7 gap-0.5 overflow-visible">
          {/* Day headers */}
          {(() => {
            const days = [];
            for (let i = 0; i < 7; i++) {
              const d = new Date(2024, 0, 7 + i); // Sun 7 -> Sat 13
              days.push(d.toLocaleDateString(locale, { weekday: 'short' }));
            }
            return days.map(day => (
              <div key={day} className="text-center text-[9px] font-semibold text-gray-600 py-0.5">
                {day}
              </div>
            ));
          })()}
          
          {/* Calendar days */}
          {calendarDays}
        </div>
      )}

      {/* Week View */}
      {viewMode === 'week' && (
        <div className="grid grid-cols-7 gap-1">
          {(() => {
            const weekDayLabels = [];
            for (let i = 1; i <= 7; i++) {
              const d = new Date(2024, 0, i);
              weekDayLabels.push(d.toLocaleDateString(locale, { weekday: 'short' }));
            }
            return weekDayLabels;
          })().map((day, index) => {
            const weekDates = getWeekDates(selectedDate);
            const date = weekDates[index];
            const dayOrders = getOrdersForDate(date);
            const isToday = date.toDateString() === today.toDateString();

            return (
              <div key={index} className="flex flex-col">
                <div className={`text-center text-xs font-semibold py-2 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                  {day} {date.getDate()}
                </div>
                <div className={`border rounded p-2 min-h-[200px] ${isToday ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'}`}>
                  <div className="space-y-1">
                    {dayOrders.map((order, idx) => (
                      <div key={idx} className="relative group">
                        <div 
                          className={`text-[9px] px-1.5 py-1 rounded cursor-pointer transition-all hover:scale-105 ${
                            order.status === 'assigned' || order.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                            order.status === 'completed' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                            'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                          onMouseEnter={() => setHoveredOrder(order.id)}
                          onMouseLeave={() => setHoveredOrder(null)}
                          onClick={() => onServiceClick && onServiceClick()}
                        >
                          <div className="font-semibold">{order.service_time}</div>
                          <div className="text-[8px] truncate">{order.pickup_address?.split(',')[0]}</div>
                        </div>
                        
                        {hoveredOrder === order.id && (
                          <div 
                            className="absolute z-[9999] bg-gray-900 text-white text-[10px] p-3 rounded-lg shadow-2xl border border-gray-700 left-full ml-2 top-0 whitespace-normal"
                            style={{ minWidth: '220px', maxWidth: '280px' }}
                            onMouseEnter={() => setHoveredOrder(order.id)}
                            onMouseLeave={() => setHoveredOrder(null)}
                          >
                            <div className="space-y-2">
                              <div>
                                <div className="font-bold text-green-400 mb-1 flex items-center gap-1">
                                  <span>📍</span> {t('calendar_origin')}
                                </div>
                                <div className="pl-4 text-gray-200 leading-relaxed">{order.pickup_address}</div>
                              </div>
                              <div className="border-t border-gray-700 pt-2">
                                <div className="font-bold text-blue-400 mb-1 flex items-center gap-1">
                                  <span>🎯</span> {t('calendar_destination')}
                                </div>
                                <div className="pl-4 text-gray-200 leading-relaxed">{order.delivery_address}</div>
                              </div>
                              <div 
                                className="text-[8px] text-center pt-2 border-t border-gray-700 cursor-pointer hover:bg-gray-800 rounded py-1 transition-colors"
                                onClick={() => {
                                  setHoveredOrder(null);
                                  onServiceClick && onServiceClick();
                                }}
                              >
                                <span className="text-blue-400 font-semibold">→ {t('calendar_view_full_details')}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {dayOrders.length === 0 && (
                      <div className="text-[8px] text-gray-400 text-center py-4">{t('calendar_no_services')}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Day View */}
      {viewMode === 'day' && (
        <div className="border rounded p-4 bg-white">
          <div className="space-y-2">
            {getOrdersForDate(selectedDate).map((order, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg cursor-pointer transition-all hover:scale-[1.02] ${
                  order.status === 'assigned' || order.status === 'in_progress' ? 'bg-yellow-50 border-2 border-yellow-200' :
                  order.status === 'completed' ? 'bg-green-50 border-2 border-green-200' :
                  'bg-gray-50 border-2 border-gray-200'
                }`}
                onClick={() => onServiceClick && onServiceClick()}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-bold">{order.service_time}</div>
                  <div className={`text-[9px] px-2 py-1 rounded font-semibold ${
                    order.status === 'assigned' || order.status === 'in_progress' ? 'bg-yellow-200 text-yellow-800' :
                    order.status === 'completed' ? 'bg-green-200 text-green-800' :
                    'bg-gray-200 text-gray-800'
                  }`}>
                    {order.status === 'assigned' ? t('calendar_legend_assigned') : order.status === 'completed' ? t('calendar_legend_completed') : order.status}
                  </div>
                </div>
                <div className="space-y-1.5 text-[10px]">
                  <div>
                    <span className="font-semibold text-green-600">📍 {t('calendar_origin')}</span>
                    <div className="pl-4 text-gray-700">{order.pickup_address}</div>
                  </div>
                  <div>
                    <span className="font-semibold text-blue-600">🎯 {t('calendar_destination')}</span>
                    <div className="pl-4 text-gray-700">{order.delivery_address}</div>
                  </div>
                </div>
              </div>
            ))}
            {getOrdersForDate(selectedDate).length === 0 && (
              <div className="text-center text-gray-400 py-8">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>{t('calendar_no_services_this_day')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex gap-2 text-[9px] justify-center">
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 bg-yellow-100 border border-yellow-300 rounded"></div>
          <span>{t('calendar_legend_assigned')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 bg-green-100 border border-green-300 rounded"></div>
          <span>{t('calendar_legend_completed')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 bg-blue-50 border border-blue-500 rounded"></div>
          <span>{t('calendar_legend_today')}</span>
        </div>
      </div>
    </div>
  );
};

const DriverDashboardHome = ({ driver, orders, documents, onTabChange, hideOrderPrices = false }) => {
  const { t, i18n } = useTranslation();
  const locale = localeForCalendar(i18n.language);

  // Get current week's orders (Monday to Sunday)
  const getCurrentWeekOrders = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to get Monday
    
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return orders.filter(order => {
      const orderDate = new Date(order.created_at || order.service_date);
      return orderDate >= monday && orderDate <= sunday;
    });
  };

  const weekOrders = getCurrentWeekOrders();

  // Calculate statistics for cards (all time)
  const stats = {
    total: orders.length,
    in_progress: orders.filter(o => 
      o.status === 'assigned' || 
      o.status === 'in_transit' || 
      o.status === 'confirmed'
    ).length,
    completed: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => 
      o.status === 'cancelled' || 
      o.status?.includes('Cancelado')
    ).length,
    total_earnings: hideOrderPrices
      ? 0
      : orders.filter(o => o.status === 'delivered').reduce((sum, o) => sum + (o.total_price * 0.7 || 0), 0), // Assume 70% commission
    documents_status: {
      total: 7,
      uploaded: documents.length,
      approved: documents.filter(d => d.status === 'approved').length,
      pending: documents.filter(d => d.status === 'pending').length,
      rejected: documents.filter(d => d.status === 'rejected').length
    }
  };

  // Calculate statistics for charts (current week only)
  const weekStats = {
    in_progress: weekOrders.filter(o => 
      o.status === 'assigned' || 
      o.status === 'in_transit' || 
      o.status === 'confirmed'
    ).length,
    completed: weekOrders.filter(o => o.status === 'delivered').length,
    cancelled: weekOrders.filter(o => 
      o.status === 'cancelled' || 
      o.status?.includes('Cancelado')
    ).length,
  };

  // Chart data for status distribution - Show all statuses even if 0 (CURRENT WEEK)
  const statusChartData = [
    { name: t('admin_status_in_progress'), value: weekStats.in_progress, color: '#3b82f6' },
    { name: t('driver_completed'), value: weekStats.completed, color: '#36e961' },
    { name: t('driver_chart_cancelled'), value: weekStats.cancelled, color: '#ef4444' }
  ];

  // Document status chart data
  const documentChartData = [
    { name: t('driver_chart_approved'), value: stats.documents_status.approved, color: '#36e961' },
    { name: t('driver_doc_pending'), value: stats.documents_status.pending, color: '#f59e0b' },
    { name: t('driver_chart_rejected'), value: stats.documents_status.rejected, color: '#ef4444' },
    { name: t('driver_chart_missing'), value: stats.documents_status.total - stats.documents_status.uploaded, color: '#6b7280' }
  ];

  // Prepare monthly trend data (last 6 months)
  const getMonthlyTrendData = () => {
    const monthNamesShort = Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i, 1).toLocaleDateString(locale, { month: 'short' })
    );
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    const data = [];
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(currentYear, currentMonth - i, 1);
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
      
      const monthOrders = orders.filter(order => {
        const orderDate = new Date(order.created_at || order.service_date);
        return orderDate >= monthStart && orderDate <= monthEnd;
      });

      data.push({
        month: monthNamesShort[monthDate.getMonth()],
        completados: monthOrders.filter(o => o.status === 'delivered').length,
        ingresos: hideOrderPrices
          ? 0
          : monthOrders.filter(o => o.status === 'delivered').reduce((sum, o) => sum + (o.total_price * 0.7 || 0), 0)
      });
    }
    
    return data;
  };

  const monthlyTrendData = getMonthlyTrendData();

  return (
    <div className="space-y-6">
      {/* Driver Status Alert */}
      {!driver?.documents_validated && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-900">{t('driver_docs_pending_title')}</h3>
              <p className="text-sm text-yellow-800 mt-1">
                {t('driver_docs_pending_text')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Services */}
        <Card className="cursor-pointer hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('driver_total_services')}</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              {t('driver_completed_count', { count: stats.completed })}
            </p>
          </CardContent>
        </Card>

        {/* In Progress */}
        <Card className="cursor-pointer hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin_status_in_progress')}</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.in_progress}</div>
            <p className="text-xs text-muted-foreground">
              {t('driver_active_services')}
            </p>
          </CardContent>
        </Card>

        {/* Total Earnings */}
        <Card className="cursor-pointer hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('total_revenue')}</CardTitle>
            <DollarSign className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {hideOrderPrices ? '—' : `€${stats.total_earnings.toFixed(2)}`}
            </div>
            <p className="text-xs text-muted-foreground">
              {hideOrderPrices ? t('driver_fleet_employee_no_tariffs') : t('driver_from_n_services', { count: stats.completed })}
            </p>
          </CardContent>
        </Card>

        {/* Document Status */}
        <Card className="cursor-pointer hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin_documents')}</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.documents_status.approved}/{stats.documents_status.total}
            </div>
            <p className="text-xs text-muted-foreground">
              {driver?.documents_validated ? (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {t('driver_validated')}
                </span>
              ) : (
                <span className="text-yellow-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {t('driver_doc_pending')}
                </span>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Calendar with Services */}
      <Card className="overflow-visible">
        <CardHeader>
          <CardTitle>{t('calendar_title')}</CardTitle>
          <CardDescription>{t('calendar_scheduled_subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="overflow-visible">
          <ServiceCalendar 
            orders={orders} 
            onServiceClick={() => onTabChange && onTabChange('orders')}
          />
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Services Status Chart */}
        <Card>
          <CardHeader>
            <CardTitle>{t('driver_status_services')}</CardTitle>
            <CardDescription>{t('driver_this_week_n_services', { count: weekOrders.length })}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => value > 0 ? `${name}: ${value}` : ''}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Documents Status Chart */}
        <Card>
          <CardHeader>
            <CardTitle>{t('driver_status_documents')}</CardTitle>
            <CardDescription>{t('driver_validation_progress', { uploaded: stats.documents_status.uploaded })}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={documentChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value, percent }) => value > 0 ? `${name}: ${value}` : ''}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {documentChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>{t('driver_trend_title')}</CardTitle>
          <CardDescription>{t('driver_trend_subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                {!hideOrderPrices && <YAxis yAxisId="right" orientation="right" />}
                <Tooltip formatter={(value, name) => [
                  name === 'completados' ? `${value}` : `€${Number(value).toFixed(2)}`,
                  name === 'completados' ? t('driver_chart_completed') : t('driver_chart_income')
                ]} />
                <Legend />
                <Bar yAxisId="left" dataKey="completados" fill="#36e961" name={t('driver_chart_completed')} />
                {!hideOrderPrices && (
                  <Bar yAxisId="right" dataKey="ingresos" fill="#3b82f6" name={`${t('driver_chart_income')} (€)`} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Recent Services */}
      {orders.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('driver_recent_services')}</CardTitle>
            <CardDescription>{t('driver_last_5_assigned')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {orders.slice(0, 5).map((order) => (
                <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-gray-600" />
                      <span className="font-medium">#{order.id?.substring(0, 8) || 'N/A'}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'assigned' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status === 'delivered' ? t('service_status_delivered') :
                         order.status === 'in_transit' ? t('service_status_in_transit') :
                         order.status === 'assigned' ? t('service_status_assigned') : order.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {order.pickup_address} → {order.delivery_address}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {order.service_date ? new Date(order.service_date).toLocaleDateString(locale) : t('driver_date_unavailable')}
                    </p>
                  </div>
                  {order.status === 'delivered' && !hideOrderPrices && (
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-600">
                        €{((order.total_price || 0) * 0.7).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">{t('driver_commission')}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DriverDashboardHome;